import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

const SENTRY_DSN = import.meta.env.VITE_APP_SENTRY_DSN_URL;

if (!import.meta.env.DEV) {
    Sentry.init({
        dsn: SENTRY_DSN,
        environment: import.meta.env.VITE_APP_ENV,
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],
        // Reduced from 1.0 to lower per-request overhead on production API calls.
        // 10% sampling is sufficient for performance insights without header overhead on every request.
        tracesSampleRate: 0.1,
        tracePropagationTargets: [/^\//, /^https:\/\/yourserver\.io\/api/],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        // Sentry v11 collects user info, cookies, unscrubbed headers and full HTTP bodies when
        // this is left unset. Our API traffic carries session tokens and planner/guest PII, so
        // pin the v10 baseline from Sentry's migration guide (BIZ-2795), plus an explicit deny
        // for the auth and cookie headers.
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: {
                request: { deny: ['authorization', 'cookie', 'forwarded', '-ip', 'remote-', 'via', '-user'] },
                response: { deny: ['set-cookie', 'forwarded', '-ip', 'remote-', 'via', '-user'] },
            },
            httpBodies: [],
            urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
            genAI: { inputs: false, outputs: false },
            databaseQueryData: false,
            queues: false,
            graphQL: { document: false, variables: false },
        },
        beforeSend(event, hint) {
            // Filter out React Fast Refresh / HMR errors from dev
            const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
            if (frames.some(f => f.filename?.includes('@react-refresh') || f.filename?.includes('react-refresh'))) {
                return null;
            }

            // Filter out non-Error unhandled rejections (plain strings/objects from API client)
            // These have no stacktrace and show as <unknown> — they're expected API errors handled by the UI
            const exceptionValue = event.exception?.values?.[0];
            if (
                exceptionValue?.type === 'UnhandledRejection' &&
                (!exceptionValue.stacktrace || !exceptionValue.stacktrace.frames?.length)
            ) {
                return null;
            }

            // A 4xx from the API client (src/api/index.js `call()`) is the server answering the
            // user (validation, a guard condition, etc.), not a defect — drop it here so it applies
            // to every `Sentry.captureException(apiError)` call site without touching each one.
            const originalException = hint?.originalException as { name?: string; status?: unknown } | undefined;
            if (
                originalException?.name === 'ApiError' &&
                typeof originalException.status === 'number' &&
                originalException.status >= 400 &&
                originalException.status < 500
            ) {
                return null;
            }

            return event;
        },
        ignoreErrors: [
            // Network errors - not actionable, caused by client connectivity issues.
            // Must be a RegExp: the string "Failed to fetch" is substring-matched and would
            // also drop "Failed to fetch dynamically imported module" (chunk load), which
            // ErrorFallback reports via captureException after a failed auto-reload.
            /^Failed to fetch$/,
            'NetworkError when attempting to fetch resource', // Firefox
            // Netlify's own RUM beacon, blocked by ad blockers and corporate filters for a
            // large share of users. Matched on the host so it catches every browser's wording
            // ("Failed to fetch (host)" in Chrome, "NetworkError ... (host)" in Firefox) —
            // the anchored /^Failed to fetch$/ above deliberately does not, since it must let
            // "Failed to fetch dynamically imported module" through. Left unfiltered this is
            // the single highest-volume event in the project and it buries real errors: it
            // accounts for ~1,800 captures across ~470 users per week in PostHog, where the
            // same beacon dominates the error list.
            /ingesteer\.services-prod\.nsvcs\.net/,
            'Load failed', // Safari
            'Network request failed',
            'The Internet connection appears to be offline', // Safari offline
            // Chunk load errors are handled in ErrorFallback: auto-reload on first hit,
            // Sentry.captureException (level: warning) on second hit if reload did not help.
            /^cancelled$/i, // Request cancelled
            /^aborted$/i, // Request aborted
            // Auth errors - expected user-facing errors, not bugs
            'Authentication required',
            // Cross-origin frame errors - Google OAuth iframe cleanup in Safari
            /Blocked a frame with origin.*from accessing a cross-origin frame/i,
            // Non-JSON API responses (server errors returning HTML, empty responses)
            /Unexpected token.*is not valid JSON/i,
            /Unexpected end of JSON input/i,
            /The string did not match the expected pattern/i, // Safari's JSON parse error
            // Third-party scripts (Intercom, extensions)
            /Object Not Found Matching Id/i,
        ],
    });
}
